/* ==========================================================
   Prime Carton — shared table behaviour
   Tabs, filtering, column sort, bundle steppers, AJAX add,
   drawer refresh, static cart quantities, default volume sort.
   Self-initialising. Safe to load on every page.
   ========================================================== */
(function () {
  'use strict';

  var ROUTES = {
    cartAdd: '/cart/add.js',
    cartJs: '/cart.js',
    cart: '/cart'
  };

  /* ---------- helpers ---------- */

  function pcClosest(el, sel) {
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(sel)) return el;
      el = el.parentElement;
    }
    return null;
  }

  /* Quantities are clamped relative to the MINIMUM, not to zero. A product
     with a 500 minimum and a 15 bundle steps 500, 515, 530. Clamping to
     step multiples instead would give 510 and never allow the true minimum. */
  function clampQty(v, min, step) {
    var n = parseInt(v, 10);
    if (isNaN(n) || n <= min) return min;
    var k = Math.ceil((n - min) / step);
    return min + (k * step);
  }

  function toast(html) {
    var el = document.getElementById('pc-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'pc-toast';
      el.className = 'pc-toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.innerHTML = html;
    requestAnimationFrame(function () { el.classList.add('pc-toast--in'); });
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.classList.remove('pc-toast--in'); }, 5000);
  }

  /* ---------- cart state ----------
     Made-to-order sizes carry a 500 minimum, but that is a production run
     minimum, not a per-order-line rule. Once the customer already holds 500
     of a variant, a top-up should start at one bundle rather than demanding
     another full run. So the effective minimum depends on what is in the
     cart, and we keep a variant id -> quantity map to decide. */

  var cartQty = {};
  var cartLoaded = false;

  function loadCartState() {
    return fetch(ROUTES.cartJs, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartQty = {};
        (cart.items || []).forEach(function (it) {
          cartQty[String(it.variant_id)] = it.quantity;
        });
        cartLoaded = true;
        applyMinimums();
      })
      .catch(function () { /* leave minimums at their server values */ });
  }

  function effectiveMin(box) {
    var min = parseInt(box.getAttribute('data-min'), 10);
    var step = parseInt(box.getAttribute('data-step'), 10) || 25;
    if (isNaN(min)) min = step;
    if (!cartLoaded) return min;
    var held = cartQty[String(box.getAttribute('data-vid'))] || 0;
    return held >= min ? step : min;
  }

  /* Re-floor every stepper after cart state changes. An untouched stepper
     follows the floor in both directions: down to one bundle once the cart
     satisfies the minimum, back up to the minimum if that line is removed.
     A touched stepper is only ever raised, never lowered. */
  function applyMinimums() {
    document.querySelectorAll('[data-pc-add]').forEach(function (box) {
      var qty = box.querySelector('.pc-add__qty');
      if (!qty) return;

      var min = effectiveMin(box);
      var step = parseInt(box.getAttribute('data-step'), 10) || 25;
      var current = parseInt(qty.value, 10);

      box.setAttribute('data-effmin', min);

      if (!box.dataset.pcTouched) {
        qty.value = min;
      } else if (isNaN(current) || current < min) {
        qty.value = clampQty(current, min, step);
      }

      var minus = box.querySelector('[data-dir="-1"]');
      if (minus) minus.disabled = (parseInt(qty.value, 10) || min) <= min;

      var note = box.querySelector('.pc-add__min');
      if (note) {
        note.textContent = min > step ? ('min ' + min) : '';
        note.hidden = !(min > step);
      }
    });
  }

  function refreshCartUI() {
    var headerSection = document.querySelector('[id^="shopify-section-sections--"][id*="__header_section"]');
    var headerId = headerSection ? headerSection.id.replace('shopify-section-', '') : null;
    var want = ['cart-drawer-section'];
    if (headerId) want.push(headerId);

    fetch('/?sections=' + want.join(','))
      .then(function (r) { return r.json(); })
      .then(function (data) {

        /* ---- cart drawer: replace the whole inner ---- */
        if (data['cart-drawer-section']) {
          var tmp = document.createElement('div');
          tmp.innerHTML = data['cart-drawer-section'];

          var freshInner = tmp.querySelector('.cart-drawer__inner');
          var comp = document.querySelector('cart-drawer-component');
          var liveInner = comp ? comp.querySelector('.cart-drawer__inner') : null;

          if (freshInner && liveInner) {
            liveInner.insertAdjacentHTML('afterend', freshInner.outerHTML);
            liveInner.remove();
          }

          /* refreshCartUI only runs after a successful add, so the cart
             is guaranteed non-empty: strip empty-state markers unconditionally.
             Do it now and again next frame in case theme code morphs attributes. */
          var clearEmpty = function () {
            var nowInner = comp ? comp.querySelector('.cart-drawer__inner') : null;
            if (nowInner) nowInner.classList.remove('cart-drawer--empty');
            if (comp) {
              comp.classList.remove('cart-drawer--empty');
              comp.removeAttribute('data-empty');
            }
          };
          clearEmpty();
          requestAnimationFrame(clearEmpty);
        }

        /* ---- header cart icon ---- */
        if (headerId && data[headerId] && headerSection) {
          var t2 = document.createElement('div');
          t2.innerHTML = data[headerId];
          var freshIcon = t2.querySelector('cart-icon');
          var liveIcon = headerSection.querySelector('cart-icon');
          if (freshIcon && liveIcon) {
            liveIcon.insertAdjacentHTML('afterend', freshIcon.outerHTML);
            liveIcon.remove();
          }
        }
      })
      .catch(function () {});
  }

  /* ---------- tabs ---------- */
  function initTabs() {
    var tabs = Array.prototype.slice.call(document.querySelectorAll('.pc-tab'));
    if (!tabs.length) return;
    var panels = Array.prototype.slice.call(document.querySelectorAll('.pc-panel'));

    tabs.forEach(function (tab) {
      if (tab.dataset.pcInit) return;
      tab.dataset.pcInit = '1';
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) {
          t.classList.remove('pc-tab--on');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('pc-tab--on');
        tab.setAttribute('aria-selected', 'true');
        var target = tab.getAttribute('data-target');
        panels.forEach(function (p) { p.classList.toggle('pc-panel--hidden', p.id !== target); });
        applyFilter();
      });
    });
  }

  /* ---------- filter ---------- */
  function activeScope() {
    var panels = Array.prototype.slice.call(document.querySelectorAll('.pc-panel'));
    if (panels.length) {
      for (var i = 0; i < panels.length; i++) {
        if (!panels[i].classList.contains('pc-panel--hidden')) return panels[i];
      }
      return null;
    }
    return document; // single-table pages
  }

  function applyFilter() {
    var input = document.getElementById('pc-filter');
    var count = document.getElementById('pc-count');
    var scope = activeScope();
    if (!scope) return;

    var rows = Array.prototype.slice.call(scope.querySelectorAll('.pc-stocktable tbody tr'));
    var q = (input ? input.value : '').trim().toLowerCase().replace(/\s+/g, '');
    var shown = 0;
    rows.forEach(function (r) {
      var hay = (r.getAttribute('data-search') || '').replace(/\s+/g, '');
      var hit = !q || hay.indexOf(q) !== -1;
      r.hidden = !hit;
      if (hit) shown++;
    });
    if (count) count.textContent = shown + ' of ' + rows.length + ' shown';
  }

  function initFilter() {
    var input = document.getElementById('pc-filter');
    if (!input || input.dataset.pcInit) return;
    input.dataset.pcInit = '1';
    input.addEventListener('input', applyFilter);
    applyFilter();
  }

  /* ---------- default sort: volumetric, small to large ----------
     Shopify cannot sort a collection by a computed value, so the server
     renders in the collection's own order and we reorder here. Runs on boot
     and again when hydration finishes, since appended rows arrive in page
     order rather than volume order.

     Skipped once the visitor has sorted a column themselves, so a late
     hydration pass cannot drag them back to volume order. */

  function sortByVolume(table) {
    var body = table.tBodies[0];
    if (!body) return;

    var rows = Array.prototype.slice.call(body.querySelectorAll('tr[data-vol]'));
    if (rows.length < 2) return;

    rows.sort(function (a, b) {
      return (parseFloat(a.getAttribute('data-vol')) || 0) -
             (parseFloat(b.getAttribute('data-vol')) || 0);
    });

    var frag = document.createDocumentFragment();
    rows.forEach(function (r) { frag.appendChild(r); });
    body.appendChild(frag);

    /* reflect it in the header so the arrow matches what is on screen, and
       so the first user click on that column flips to descending */
    if (table.tHead) {
      Array.prototype.slice.call(table.tHead.querySelectorAll('th[aria-sort]')).forEach(function (o) {
        o.removeAttribute('aria-sort');
      });
      var th = table.tHead.querySelector('th[data-pc-default]');
      if (th) th.setAttribute('aria-sort', 'ascending');
    }
  }

  function initDefaultSort() {
    document.querySelectorAll('table.pc-jstable').forEach(function (table) {
      if (table.getAttribute('data-pc-usersorted')) return;
      /* boot() re-runs on every DOM mutation, and sortByVolume IS a DOM
         mutation, so an unguarded call here loops forever re-appending the
         tbody every frame. Sort once, mark it, skip until something
         (hydration) clears the mark. */
      if (table.getAttribute('data-pc-volsorted')) return;
      table.setAttribute('data-pc-volsorted', '1');
      sortByVolume(table);
    });
  }

  /* ---------- column sort ---------- */
  function initSort() {
    document.querySelectorAll('table.pc-jstable').forEach(function (table) {
      if (table.dataset.pcInit) return;
      table.dataset.pcInit = '1';
      var body = table.tBodies[0];
      if (!body || !table.tHead) return;

      var heads = [];
      Array.prototype.slice.call(table.tHead.rows).forEach(function (hr) {
        Array.prototype.slice.call(hr.cells).forEach(function (c) {
          if (c.getAttribute('data-sort')) heads.push(c);
        });
      });

      heads.forEach(function (th) {
        th.addEventListener('click', function () {
          /* read rows at click time, not at init: hydration appends rows
             after this handler is bound, and a captured list would leave
             them stranded at the bottom of every sort */
          var rows = Array.prototype.slice.call(body.rows);

          table.setAttribute('data-pc-usersorted', '1');

          var idx = heads.indexOf(th);
          var mode = th.getAttribute('data-sort');
          var desc = th.getAttribute('aria-sort') === 'ascending';
          heads.forEach(function (o) { o.removeAttribute('aria-sort'); });
          th.setAttribute('aria-sort', desc ? 'descending' : 'ascending');

          var sorted = rows.slice().sort(function (a, b) {
            var ac = a.cells[idx], bc = b.cells[idx];
            if (!ac || !bc) return 0;
            var av = ac.getAttribute('data-v') || ac.textContent;
            var bv = bc.getAttribute('data-v') || bc.textContent;
            if (mode === 'num') return (parseFloat(av) || 0) - (parseFloat(bv) || 0);
            return String(av).localeCompare(String(bv), undefined, { numeric: true });
          });
          if (desc) sorted.reverse();
          sorted.forEach(function (r) { body.appendChild(r); });
        });
      });
    });
  }

  /* ---------- per-row bundle stepper + add ---------- */
  var addInFlight = false;

  function initAdders() {
    document.querySelectorAll('[data-pc-add]').forEach(function (box) {
      if (box.dataset.pcInit) return;
      box.dataset.pcInit = '1';

      var step = parseInt(box.getAttribute('data-step'), 10) || 25;
      var qty = box.querySelector('.pc-add__qty');
      var go = box.querySelector('.pc-add__go');
      var minus = box.querySelector('[data-dir="-1"]');
      if (!qty || !go) return;

      // remember the original label so it can always be restored
      if (!go.dataset.pcLabel) go.dataset.pcLabel = go.textContent.trim() || 'Add';

      function min() { return effectiveMin(box); }

      function sync() {
        var m = min();
        var n = parseInt(qty.value, 10) || m;
        if (minus) minus.disabled = n <= m;
      }

      function reset() {
        go.textContent = go.dataset.pcLabel;
        go.classList.remove('pc-add__go--done');
        go.disabled = false;
      }

      box.querySelectorAll('.pc-add__btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var m = min();
          var dir = parseInt(btn.getAttribute('data-dir'), 10);
          var next = clampQty(qty.value, m, step) + (dir * step);
          if (next < m) next = m;
          qty.value = next;
          box.dataset.pcTouched = '1';
          sync();
        });
      });

      qty.addEventListener('blur', function () {
        qty.value = clampQty(qty.value, min(), step);
        box.dataset.pcTouched = '1';
        sync();
      });

      qty.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          qty.value = clampQty(qty.value, min(), step);
          box.dataset.pcTouched = '1';
          sync();
          go.click();
        }
      });

      sync();

      go.addEventListener('click', function () {
        // ignore clicks while any add is still in flight
        if (addInFlight || go.disabled) return;
        addInFlight = true;

        go.disabled = true;
        go.textContent = 'Adding…';

        // hard safety net: never leave the button stuck
        var bail = setTimeout(function () {
          addInFlight = false;
          reset();
        }, 8000);

        /* GUARD: the on-screen floor can be stale (the customer may have
           removed the qualifying line in the drawer, which we cannot see
           happen). Re-read the cart and re-clamp before posting, so the
           quantity that reaches Shopify always respects the real minimum. */
        loadCartState()
          .then(function () {
            var m = min();
            var n = clampQty(qty.value, m, step);
            if (parseInt(qty.value, 10) !== n) {
              qty.value = n;
              toast('<span>Minimum for this size is ' + m + ' boxes. Quantity adjusted.</span>');
            }

            return fetch(ROUTES.cartAdd, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: box.getAttribute('data-vid'), quantity: n })
            })
              .then(function (r) {
                if (!r.ok) throw new Error('cart add failed');
                return r.json();
              })
              .then(function () {
                clearTimeout(bail);
                go.textContent = 'Added';
                go.classList.add('pc-add__go--done');
                toast('<span>' + n + ' \u00d7 ' + box.getAttribute('data-title') + ' added.</span>' +
                      '<a href="' + ROUTES.cart + '">View cart</a>');
                refreshCartUI();

                /* the customer may now hold the minimum, which drops this
                   row's floor to one bundle for any top-up */
                box.dataset.pcTouched = '';
                loadCartState();

                addInFlight = false;
                setTimeout(reset, 1600);
              });
          })
          .catch(function () {
            clearTimeout(bail);
            addInFlight = false;
            go.textContent = 'Try again';
            setTimeout(reset, 2000);
          });
      });
    });
  }

  /* ---------- static cart quantities ---------- */
  function initStaticQty() {
    document.querySelectorAll('.cart-items__quantity-controls').forEach(function (wrap) {
      if (wrap.querySelector('.pc-qty-static')) return;
      var input = wrap.querySelector('input[name="updates[]"]');
      var qty = input ? (input.getAttribute('data-cart-quantity') || input.value) : null;
      if (!qty) return;
      var span = document.createElement('span');
      span.className = 'pc-qty-static';
      span.innerHTML = qty + ' boxes<small>Remove and re-add to change</small>';
      wrap.insertBefore(span, wrap.firstChild);
    });
  }

  /* ---------- full-catalogue hydration ----------
     Liquid caps collection.products at 50 rows per loop, so any table over
     50 products renders truncated while the tab badge shows the true count.
     Each table declares its source collection and page count; we fetch the
     remaining pages from the collection URL and append their rows.

     Lazy: a hidden tab panel is not hydrated until it is opened, or until
     the filter is used (at which point every table is pulled in full, since
     a filter that only sees page one is worse than useless). */

  function needsHydration(table) {
    if (table.getAttribute('data-pc-hydrated')) return false;
    if (table.getAttribute('data-pc-hydrating')) return false;
    var pages = parseInt(table.getAttribute('data-pc-pages'), 10);
    return !!table.getAttribute('data-pc-collection') && pages > 1;
  }

  function hydrate(table) {
    if (!needsHydration(table)) return;
    table.setAttribute('data-pc-hydrating', '1');

    var handle = table.getAttribute('data-pc-collection');
    var pages = parseInt(table.getAttribute('data-pc-pages'), 10);
    var tbody = table.querySelector('tbody');
    if (!tbody) { table.removeAttribute('data-pc-hydrating'); return; }

    var seen = {};
    Array.prototype.slice.call(tbody.querySelectorAll('tr[data-search]')).forEach(function (tr) {
      seen[tr.getAttribute('data-search')] = 1;
    });

    var chain = Promise.resolve();

    /* which page is this document already showing? Fetch every page EXCEPT
       this one. On page 1 that means 2..N; on page 2 it means 1, 3..N.
       Fetching our own page re-added nothing and left the rest missing,
       which stranded direct visits to ?page=2 at 50 rows with the pager
       hidden: a dead end. */
    var here = parseInt(new URLSearchParams(location.search).get('page'), 10) || 1;

    for (var p = 1; p <= pages; p++) {
      if (p === here) continue;
      (function (page) {
        chain = chain.then(function () {
          return fetch('/collections/' + handle + '?page=' + page)
            .then(function (r) {
              if (!r.ok) throw new Error('page ' + page + ' failed');
              return r.text();
            })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var src = doc.querySelector('table.pc-jstable tbody');
              if (!src) return;
              var frag = document.createDocumentFragment();
              Array.prototype.slice.call(src.querySelectorAll('tr[data-search]')).forEach(function (tr) {
                var key = tr.getAttribute('data-search');
                if (seen[key]) return;
                seen[key] = 1;
                frag.appendChild(document.importNode(tr, true));
              });
              tbody.appendChild(frag);
            });
        });
      })(p);
    }

    chain
      .then(function () { table.setAttribute('data-pc-hydrated', '1'); })
      .catch(function () { /* partial rows are better than none */ })
      .then(function () {
        table.removeAttribute('data-pc-hydrating');

        // every row is now on this page, so the pager is redundant
        var scope = pcClosest(table, '.pc-panel') || pcClosest(table, '.pc-sec__inner');
        var pager = scope ? scope.querySelector('[data-pc-paginate]') : null;
        if (pager) pager.hidden = true;

        initAdders();
        applyMinimums();

        /* appended rows arrive in page order, so re-sort unless the visitor
           has already chosen a column */
        if (!table.getAttribute('data-pc-usersorted')) {
          table.setAttribute('data-pc-volsorted', '1');
          sortByVolume(table);
        }

        applyFilter();
      });
  }

  /* Only tables the visitor can actually see. */
  function initHydrate() {
    Array.prototype.slice.call(document.querySelectorAll('table[data-pc-collection]')).forEach(function (t) {
      var panel = pcClosest(t, '.pc-panel');
      if (!panel || !panel.classList.contains('pc-panel--hidden')) hydrate(t);
    });
  }

  function hydrateAll() {
    Array.prototype.slice.call(document.querySelectorAll('table[data-pc-collection]')).forEach(hydrate);
  }

  /* Delegated so this stays additive: initTabs and initFilter are untouched. */
  document.addEventListener('click', function (e) {
    if (pcClosest(e.target, '.pc-tab')) setTimeout(initHydrate, 0);
  });
  document.addEventListener('input', function (e) {
    if (e.target && e.target.id === 'pc-filter') hydrateAll();
  });

  /* ---------- boot ---------- */
  var lastCartPing = 0;
  function boot() {
    initTabs();
    initFilter();
    initSort();
    initDefaultSort();
    initAdders();
    initStaticQty();
    initHydrate();

    /* drawer removals re-render cart markup, which lands us here via the
       MutationObserver; re-read the cart so stepper floors track reality.
       Throttled: boot runs on every mutation batch. */
    if (document.querySelector('[data-pc-add]') && Date.now() - lastCartPing > 1500) {
      lastCartPing = Date.now();
      loadCartState();
    }
  }

  var bootQueued = false;
  function bootSoon() {
    if (bootQueued) return;
    bootQueued = true;
    requestAnimationFrame(function () { bootQueued = false; boot(); });
  }

  boot();
  document.addEventListener('DOMContentLoaded', boot);

  new MutationObserver(bootSoon).observe(document.documentElement, { childList: true, subtree: true });
})();